// # 폰을 서버에 걸고 떼는 길
//
// 셸이 공급자(APNs·FCM)에게서 받은 토큰을 서버 주소록에 건다. 걸어 둔 토큰은 `keep`
// 에 적어 둔다 — 공급자가 토큰을 갈면 **옛 것을 떼야** 죽은 주소로 쏘지 않고, 로그아웃할
// 때도 무엇을 뗄지 알아야 한다. 토큰은 비밀이 아니다(그것만으로는 아무것도 못 보낸다).
//
// 실패는 삼킨다. 푸시가 안 걸렸다고 로그인이나 로그아웃이 막히면 그쪽이 더 큰 사고다.

import type { PushSource, PushToken } from '@maru/platform'
import { useEffect } from 'react'

export const PUSH_KEY = 'push-token'

export interface PushApi {
  push_register(device: PushToken): Promise<void>
  push_unregister(token: string): Promise<void>
  push_web_key(): Promise<string | null>
}

export interface PushKeep {
  get(key: string): Promise<string | null>
  put(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
}

/** 토큰 하나를 건다. 전에 건 것과 다르면 옛 것을 뗀다. */
export async function hold_push(fresh: PushToken, api: PushApi, keep: PushKeep): Promise<void> {
  const before = await keep.get(PUSH_KEY)
  await api.push_register(fresh)
  await keep.put(PUSH_KEY, fresh.token)
  if (before !== null && before !== fresh.token) await api.push_unregister(before).catch(() => {})
}

/** 로그아웃 **전에** 부른다 — 자리를 비운 뒤엔 뗄 권한이 없다. */
export async function drop_push(api: PushApi, keep: PushKeep): Promise<void> {
  const before = await keep.get(PUSH_KEY)
  if (before === null) return
  await api.push_unregister(before).catch(() => {})
  await keep.remove(PUSH_KEY)
}

/** 지금 토큰을 받아 온다. 웹 푸시면 서버 키를 먼저 묻고, 서버가 안 켰으면 `null`. */
export async function fresh_token(source: PushSource, api: PushApi): Promise<PushToken | null> {
  if (!source.needs_key) return source.token()
  const key = await api.push_web_key()
  return key === null ? null : source.token(key)
}

/** 들어와 있는 동안 토큰을 서버와 맞춘다. 공급자가 없으면(데스크톱 셸) 아무것도 안 한다. */
export function usePush(source: PushSource | null, api: PushApi, keep: PushKeep): void {
  useEffect(() => {
    if (source === null) return
    const hold = (t: PushToken) => void hold_push(t, api, keep).catch(() => {})
    void fresh_token(source, api)
      .then((t) => t && hold(t))
      .catch(() => {})
    return source.changed(hold)
  }, [source, api, keep])
}
